import { PetType } from '@prisma/client';
import { IPet } from '../contexts/plan';
import {
  GROUP_DISCOUNT_PERCENTAGE,
  GROUP_STAMP_DUTY,
  PLAN_BILL_CYCLE,
  TAX_PERCENTAGE,
} from './constant';

export const priceFormatted = (value: number, _currency: string) =>
  Intl.NumberFormat('en-MY', { style: 'currency', currency: _currency })
    .format(value)
    .replace(/(\.|,)00$/g, '')
    .replace(/\s/g, '');

export type PricingBreakdown = {
  yearlyPrice: number;
  monthlyPrice: number;

  ncdPercent?: number;
  totalNcd?: number;

  yearlyNcdAmount: number;
  yearlyNcdPercent: number;
  totalDiscountAmount?: number;
  totalTax: number;
  totalPayable: number; // with sst and stampduty

  firstMonthPayable: number;
  firstMonthPayableWithTax: number;
  monthlyPayable: number;
  monthlyTax: number;

  totalYearly: number; // exclude tax and stamp duty
  totalYearlyForMonthly: number; // exclude tax and stamp duty

  loadingPercent: number;
  totalLoadingAmount: number;

  stampDuty: number;
  discountAmount?: number;
  totalTaxForMonthly: number;

  policy: PricePolicyBreakdown[];
};

export type PricePolicyBreakdown = {
  id: string;
  petType: PetType;
  petName: string;
  plan: string;
  yearlyPrice: number;
  monthlyPrice: number;
  monthlyNCDAmount: number;
  monthlyTax: number;
  monthlyDiscount: number;
  monthlyAmount: number;
  monthlyLoading: number;
  monthlyOneYearAmount: number;

  yearlyAmount: number;
  yearlyNcdAmount: number;
  taxAmount: number;
  discountAmount?: number;
  ncdAmount: number;
  billCycle: PLAN_BILL_CYCLE;

  yearlyTotalLoadingAmount: number;
  monthlyTotalLoadingAmount: number;

  loadingPercent: number;
  parentPolicyId?: string;
  userPetPolicyId?: string;
};

const priceBreakdown = (
  priceCycle: PLAN_BILL_CYCLE,
  pets: IPet[]
): PricingBreakdown => {
  const sortedPetsWithPrice =
    pets && pets.length > 0
      ? [...pets.filter((p) => !!p.policy)].sort(
          (a, b) => b!.policy!.yearlyPrice! - a!.policy!.yearlyPrice!
        )
      : [];

  /**
   * get entire year loading amount
   * @param billCycle
   * @param loadingPercent
   * @param yearlyPrice
   * @param monthlyPrice
   * @returns
   */
  const calLoadingPercent = (
    billCycle: PLAN_BILL_CYCLE,
    loadingPercent: number,
    yearlyPrice: number,
    monthlyPrice: number
  ) => {
    const yearlyTotalLoadingAmount =
      Math.round(yearlyPrice * loadingPercent) / 100;

    const monthlyTotalLoadingAmount =
      Math.round(monthlyPrice * 12 * loadingPercent) / 100;
    return { yearlyTotalLoadingAmount, monthlyTotalLoadingAmount };
  };

  const yearlyNcdPercent = pets?.[0]?.yearlyNcdPercent || 0.0;
  const ncdPercent =
    priceCycle === PLAN_BILL_CYCLE.YEARLY ? yearlyNcdPercent : 0;
  const petPolicy = [] as PricePolicyBreakdown[];
  /* eslint-disable react/no-array-index-key */
  // sorted to calculate discount
  sortedPetsWithPrice.forEach((pet, index) => {
    const { policy, loadingPercent } = pet;
    const { yearlyPrice, monthlyPrice } = policy!;
    // group discount for yearly only
    const discountAmount =
      index > 0 && priceCycle === PLAN_BILL_CYCLE.YEARLY
        ? Number((yearlyPrice * GROUP_DISCOUNT_PERCENTAGE).toFixed(2))
        : 0.0;
    let yearlyTotalLoadingAmount = 0.0;
    let monthlyTotalLoadingAmount = 0.0;
    if (loadingPercent && loadingPercent > 0) {
      const loadingCalcRes = calLoadingPercent(
        priceCycle,
        loadingPercent ?? 0,
        yearlyPrice,
        monthlyPrice
      );
      yearlyTotalLoadingAmount = loadingCalcRes.yearlyTotalLoadingAmount;
      monthlyTotalLoadingAmount = loadingCalcRes.monthlyTotalLoadingAmount;
    }

    const monthlyNCDAmount = 0;
    const monthlyBasePrice =
      priceCycle === PLAN_BILL_CYCLE.YEARLY
        ? (yearlyPrice - discountAmount) / 12
        : monthlyPrice;
    const monthlyLoading =
      Math.round((monthlyTotalLoadingAmount / 12) * 100) / 100;
    const monthlyOneYearAmount =
      Math.round((monthlyPrice + monthlyLoading) * 12 * 100) / 100;

    const yearlyAmount =
      priceCycle === PLAN_BILL_CYCLE.YEARLY
        ? yearlyPrice - discountAmount + yearlyTotalLoadingAmount
        : monthlyOneYearAmount;
    const yearlyNcdAmount = Math.round(yearlyAmount * ncdPercent) / 100;

    const ncdAmount =
      priceCycle === PLAN_BILL_CYCLE.YEARLY
        ? yearlyAmount * ncdPercent
        : monthlyNCDAmount * 12;

    const monthlyAmount = monthlyBasePrice + monthlyLoading - monthlyNCDAmount;
    const monthlyTax = Math.round(monthlyAmount * TAX_PERCENTAGE * 100) / 100;

    const taxAmount =
      Math.round((yearlyAmount - yearlyNcdAmount) * TAX_PERCENTAGE * 100) / 100;

    const monthlyDiscount = Math.round((discountAmount / 12) * 100) / 100;

    petPolicy.push({
      ...pet,
      ...{ ...policy, id: pet.id },
      loadingPercent: pet?.loadingPercent || 0.0,
      plan: policy?.plan || '',
      yearlyPrice: policy?.yearlyPrice || 0,
      monthlyPrice: policy?.monthlyPrice || 0,
      billCycle: pet.billCycle ?? PLAN_BILL_CYCLE.YEARLY,
      monthlyAmount,
      monthlyLoading,
      monthlyNCDAmount,
      monthlyTax,
      monthlyDiscount,
      monthlyOneYearAmount,
      discountAmount,
      ncdAmount,
      yearlyAmount,
      yearlyNcdAmount,
      taxAmount,
      yearlyTotalLoadingAmount,
      monthlyTotalLoadingAmount,
    });
  });

  const stampDuty = GROUP_STAMP_DUTY / 100;

  const totalDiscountAmount = petPolicy.reduce(
    (sum: number, prev: { discountAmount?: number }) =>
      prev.discountAmount ? sum + prev.discountAmount : sum,
    0
  );

  const totalYearlyPrice = Math.round(
    petPolicy.reduce((sum: number, p: PricePolicyBreakdown) => {
      return sum + p.yearlyPrice;
    }, 0)
  );
  const totalMonthlyPrice = Math.round(
    petPolicy.reduce((sum: number, p: PricePolicyBreakdown) => {
      return sum + p.monthlyPrice;
    }, 0)
  );

  const totalPriceYearlyForMonthly = petPolicy.reduce(
    (sum: number, p: PricePolicyBreakdown) => {
      return sum + p.monthlyOneYearAmount;
    },
    0
  );

  const totalYearlyNcd = petPolicy.reduce(
    (sum: number, p: PricePolicyBreakdown) => {
      return sum + p.yearlyNcdAmount;
    },
    0
  );

  const totalNcd = priceCycle === PLAN_BILL_CYCLE.YEARLY ? totalYearlyNcd : 0;

  const totalTax = petPolicy.reduce((sum: number, p: PricePolicyBreakdown) => {
    return sum + p.taxAmount;
  }, 0);
  const totalMonthlyTax = petPolicy.reduce(
    (sum: number, p: PricePolicyBreakdown) => {
      return sum + p.monthlyTax;
    },
    0
  );

  const totalYearly = petPolicy.reduce(
    (sum: number, p: PricePolicyBreakdown) => {
      return sum + p.yearlyAmount;
    },
    0
  );

  const totalMonthly = petPolicy.reduce(
    (sum: number, p: PricePolicyBreakdown) => {
      return sum + p.monthlyAmount;
    },
    0
  );

  const totalLoadingAmount = petPolicy.reduce(
    (v, pol) =>
      v +
      ((priceCycle === PLAN_BILL_CYCLE.YEARLY
        ? pol.yearlyTotalLoadingAmount
        : pol.monthlyTotalLoadingAmount) || 0.0),
    0.0
  );

  const totalPayable =
    priceCycle === PLAN_BILL_CYCLE.YEARLY
      ? totalYearly + stampDuty + totalTax - totalYearlyNcd
      : totalMonthly + stampDuty + totalMonthlyTax;
  const totalTaxForMonthly =
    Math.round(totalPriceYearlyForMonthly * TAX_PERCENTAGE * 100) / 100;
  const loadingPercent =
    petPolicy.length > 0 ? petPolicy[0].loadingPercent : 0.0;

  // monthly with loading
  const monthlyPayable = petPolicy.reduce(
    (sum: number, p: PricePolicyBreakdown) => {
      return sum + p.monthlyAmount;
    },
    0
  );

  const monthlyTax = Math.round(monthlyPayable * TAX_PERCENTAGE * 100) / 100;
  const firstMonthPayable = monthlyPayable + stampDuty;
  const firstMonthPayableWithTax = firstMonthPayable + monthlyTax;

  const res = {
    yearlyPrice: totalYearlyPrice,
    monthlyPrice: totalMonthlyPrice,

    ncdPercent,
    totalNcd,
    yearlyNcdAmount: totalYearlyNcd,
    yearlyNcdPercent,
    totalDiscountAmount,
    totalTax,
    stampDuty,
    totalPayable,
    totalYearly,
    totalYearlyForMonthly: totalPriceYearlyForMonthly,

    firstMonthPayable,
    firstMonthPayableWithTax,
    monthlyPayable,
    monthlyTax,

    loadingPercent: loadingPercent || 0.0,
    totalLoadingAmount,

    totalTaxForMonthly,
    policy: petPolicy,
  };
  return res;
};

export default priceBreakdown;
